import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { ArrowRight, Handshake, Mail, MapPin } from "lucide-react";
import type { InferGetServerSidePropsType, NextPage } from "next";
import Head from "next/head";
import NextImage from "next/image";
import { useEffect, useRef } from "react";
import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import IMG_ARNOLD from "@/img/partners/arnold.jpeg";
import IMG_BFA from "@/img/partners/BFA.jpeg";
import IMG_CONCIERGE from "@/img/partners/concierge-caregiving.png";
import IMG_ELITEFTS from "@/img/partners/elitefts.png";
import IMG_FOODA from "@/img/partners/fooda.jpeg";
import IMG_HARDBODY from "@/img/partners/hardbody.png";
import IMG_ROOTS from "@/img/partners/roots.jpeg";
import IMG_WFF from "@/img/partners/WFF.jpeg";
import IMG_WESTSIDE from "@/img/partners/westside.jpeg";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";

gsap.registerPlugin(ScrollTrigger);

const OUR_PARTNERS_QUERY = b.query("OurPartners", (b) => [
  //
  b.__fragment(HEADER_FRAGMENT),
  b.pickupLocations((b) => [b.id(), b.name(), b.address((b) => [b.hash(), b.city()])]),
]);

const partners = [
  {
    src: IMG_CONCIERGE,
    name: "Concierge Caregiving",
    href: "https://conciergecaregivingcolumbus.com/",
  },
  { src: IMG_ELITEFTS, name: "Elite FTS", href: "https://www.elitefts.com/" },
  { src: IMG_ARNOLD, name: "Arnold Sports Festival", href: "https://www.arnoldsports.com/" },
  {
    src: IMG_BFA,
    name: "Balanced Family Academy",
    href: "https://www.balancedfamilyacademy.com/",
  },
  { src: IMG_FOODA, name: "Fooda", href: "https://www.fooda.com/" },
  { src: IMG_ROOTS, name: "Roots", href: "https://rootswellnessbar.square.site/" },
  { src: IMG_WESTSIDE, name: "Westside", href: "https://www.westside-barbell.com/" },
  { src: IMG_WFF, name: "White Feather Farms", href: "https://whitefeatherfarmsohio.com/" },
  { src: IMG_HARDBODY, name: "Hardbody", href: "https://hardbodyathlete.com/" },
];

const OurPartners: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const { data } = useHydratedQuery(props);
  const heroRef = useRef<HTMLDivElement>(null);
  const logosRef = useRef<HTMLDivElement>(null);
  const locationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const ctx = gsap.context(() => {
      gsap.from(hero.querySelectorAll("[data-hero-anim]"), {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.12,
        ease: "power3.out",
      });
    }, hero);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const el = logosRef.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        el.querySelectorAll("[data-logo]"),
        { y: 40, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.5,
          stagger: 0.08,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
          },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const el = locationsRef.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      gsap.from(el.querySelector("[data-section-header]"), {
        y: 40,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
        },
      });

      gsap.from(el.querySelectorAll("[data-location]"), {
        y: 20,
        opacity: 0,
        duration: 0.4,
        stagger: 0.04,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
        },
      });
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <Head>
        <title>Our Partners | Visionary Meals</title>
        <meta
          name="description"
          content="Meet the incredible partners helping us bring healthy living to life. From local gyms to national brands, we're building a better future—together."
        />
      </Head>
      <PageLayout data={data} noPadding>
        {/* ---- Hero ---- */}
        <section ref={heroRef} className="relative overflow-hidden">
          <div
            className="absolute inset-0 -z-10"
            style={{
              background: "linear-gradient(135deg, #8debcf15 0%, #ffffff 50%, #efeff108 100%)",
            }}
          />
          <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24 text-center">
            <h1
              data-hero-anim=""
              className="inline-flex items-center justify-center gap-3 text-3xl lg:text-5xl font-bold text-brand-black tracking-tight leading-tight"
            >
              <Handshake className="size-8 lg:size-10 text-brand-mint" />
              Our Partners
            </h1>
            <p
              data-hero-anim=""
              className="text-base text-gray-500 mt-4 max-w-lg mx-auto leading-relaxed"
            >
              Our mission & vision wouldn&apos;t be possible without the support of our amazing
              partners. Whether you&apos;re a local business, a gym, or a big corporation, team up
              with Visionary Meals & commit to a healthier lifestyle today!
            </p>
          </div>
        </section>

        {/* ---- Partner Logos ---- */}
        <section ref={logosRef} className="py-12 lg:py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              {partners.map((partner) => (
                <a
                  key={partner.src.src}
                  href={partner.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-logo=""
                  className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white p-6 lg:p-8 hover:border-brand-mint/40 hover:shadow-lg hover:shadow-brand-mint/5 hover:scale-[1.02] transition-all opacity-0 no-underline"
                >
                  <NextImage
                    src={partner.src}
                    alt={partner.name}
                    width={180}
                    height={180}
                    className="object-contain max-h-24"
                  />
                  <p className="text-xs font-medium text-gray-500 mt-3">{partner.name}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ---- Pickup Locations ---- */}
        <section ref={locationsRef} className="bg-gray-50 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto px-6">
            <h2
              data-section-header=""
              className="flex items-center gap-3 text-2xl lg:text-3xl font-bold text-brand-black tracking-tight"
            >
              <span className="flex items-center justify-center size-9 rounded-xl bg-brand-mint/15 shrink-0">
                <MapPin className="size-4 text-brand-mint" />
              </span>
              Pickup Locations
            </h2>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.pickupLocations.map((location) => (
                <div
                  key={location.id}
                  data-location=""
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5"
                >
                  <span className="flex items-center justify-center size-8 rounded-lg bg-brand-mint/10 shrink-0">
                    <MapPin className="size-3.5 text-brand-mint" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-brand-black truncate">{location.name}</p>
                    <p className="text-xs text-gray-500">{location.address.city}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---- CTA ---- */}
        <section className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <div className="rounded-2xl bg-gradient-to-br from-brand-mint/20 to-white border border-brand-mint/20 p-8 lg:p-12 text-center">
              <h2 className="text-2xl lg:text-3xl font-bold text-brand-black">Become a Partner</h2>
              <p className="text-sm text-gray-600 mt-3 max-w-md mx-auto">
                Interested in partnering with Visionary Meals? We&apos;d love to hear from you.
              </p>
              <a
                href="mailto:info@visionarymeals.com"
                className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-all no-underline"
              >
                <Mail className="size-4" />
                Get in Touch
                <ArrowRight className="size-4" />
              </a>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
};

export default OurPartners;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  return {
    props: await getApolloSSRProps(OUR_PARTNERS_QUERY.document(), {}, ctx),
  };
});
